import {
    AccordionDetails,
    AccordionSummary,
    List,
    ListItem,
    ListItemText,
    SxProps,
    Theme,
    Typography
} from '@mui/material';
import React, { FormEvent, useState } from 'react';
import {
    FrequentlyAskedQuestion,
    SupportContact,
    SupportDataDto,
    SupportInformation
} from '../../lib/models/SupportModels';
import {
    StyledAccordionContainer,
    StyledGlobalSwitch,
    StyledInnerAccordion
} from '../ConfigurePage.styles';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import AvailabilityModal from './AvailabilityModal';
import ContactModal from './ContactModal';
import QnAModal from './QnAModal';
import { EMPTY_GUID } from '../../lib/constants/misc';
import { post } from '../../lib/api';
import ConditionedSnackbar from '../ConditionedSnackbar';
import t from '../../theme';

interface SupportSectionProps {
    dto: SupportDataDto;
    isExpanded: boolean;
}

const SupportSection = (props: SupportSectionProps) => {
    const { technicalSupport, clinicalSupport, frequentlyAskedQuestion } = props.dto;

    const [FAQ, setFAQ] = useState(frequentlyAskedQuestion);
    const [ClinicalContact, setClinicalContact] = useState(clinicalSupport?.supportContact ?? []);
    const [TechnicalContact, setTechnicalContact] = useState(
        technicalSupport?.supportContact ?? []
    );
    const [TechnicalAvailability, setTechnicalAvailability] = useState(
        technicalSupport?.availability ?? ''
    );
    const [ClinicalAvailability, setClinicalAvailability] = useState(
        clinicalSupport?.availability ?? ''
    );
    const ClinicalSupportType = clinicalSupport?.type ?? 'Clinical';
    const TechnicalSupportType = technicalSupport?.type ?? 'Technical';
    const [TechnicalSupportId, setTechnicalSupportId] = useState(
        technicalSupport?.id ?? EMPTY_GUID
    );
    const [ClinicalSupportId, setClinicalSupportId] = useState(clinicalSupport?.id ?? EMPTY_GUID);

    const emptyTechnicalContactItem: SupportContact = {
        id: EMPTY_GUID,
        supportInformationId: TechnicalSupportId,
        supportType: '',
        phoneNumber: '',
        email: '',
        isActive: true
    };

    const emptyClinicalContactItem: SupportContact = {
        id: EMPTY_GUID,
        supportInformationId: ClinicalSupportId,
        supportType: '',
        phoneNumber: '',
        email: '',
        isActive: true
    };

    const sectionTitle = 'Support';
    const clinicalSupportTitle = 'Clinical Support';
    const technicalSupportTitle = 'Technical Support';
    const faqTitle = 'FAQ';
    const availabilityTitle = 'Availability';

    const [errored, setErrored] = useState(false);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarArgument, setSnackbarArgument] = useState<string>('');
    const snackBarMessage = (functionName: string) => {
        switch (functionName) {
            case 'toggle-faq':
                return `Question ${errored ? 'was not' : 'was'} succesfully toggled.`;
            case 'toggle-contact':
                return `Contact ${errored ? 'was not' : 'was'} succesfully toggled.`;
            case 'faq':
                return `Frequently Asked Question ${
                    errored ? 'was not' : 'was'
                } succesfully saved.`;
            case 'contact':
                return `Contact information ${errored ? 'was not' : 'was'} succesfully saved.`;
            case 'availability':
                return `Availability information ${errored ? 'was not' : 'was'} succesfully saved.`;
            default:
                return '';
        }
    };

    const onUpsertFAQ = async (event: FormEvent, faq: FrequentlyAskedQuestion) => {
        event.preventDefault();

        try {
            const result = await post(`appconfigs/upsertfrequentlyaskedquestionasync`, faq);
            result.data === '' ? setErrored(true) : setErrored(false);

            if (faq.id === EMPTY_GUID) {
                const updatedFAQList = FAQ?.concat(result.data);
                setFAQ(updatedFAQList);
            }
        } catch (error) {
            setErrored(true);
        }

        setSnackbarArgument('faq');
        setShowSnackbar(true);
    };

    const onUpsertContact = async (event: FormEvent, contact: SupportContact) => {
        event.preventDefault();

        try {
            const result = await post(`appconfigs/upsertsupportcontactasync`, contact);
            result.data === '' ? setErrored(true) : setErrored(false);

            if (contact.id === EMPTY_GUID) {
                if (contact.supportType === '') {
                    const updatedContactList = TechnicalContact.concat(result.data);
                    setTechnicalContact(updatedContactList);
                }

                if (contact.supportType !== '') {
                    const updatedContactList = ClinicalContact.concat(result.data);
                    setClinicalContact(updatedContactList);
                }
            }
        } catch (error) {
            setErrored(true);
        }

        setSnackbarArgument('contact');
        setShowSnackbar(true);
    };

    const onUpsertAvailability = async (event: FormEvent, availability: SupportInformation) => {
        event.preventDefault();

        try {
            const result = await post(`appconfigs/upsertsupportinformationasync`, availability);
            result.data === '' ? setErrored(true) : setErrored(false);

            if (result.data.type === ClinicalSupportType) {
                setClinicalAvailability(result?.data?.availability);
                setClinicalSupportId(result.data.id);
            }

            if (result.data.type === TechnicalSupportType) {
                setTechnicalAvailability(result.data.availability);
                setTechnicalSupportId(result.data.id);
            }
        } catch (error) {
            setErrored(true);
        }

        setSnackbarArgument('availability');
        setShowSnackbar(true);
    };

    const handleFAQToggle = async (faq: FrequentlyAskedQuestion) => {
        const faqUpdate: FrequentlyAskedQuestion = {
            question: faq.question,
            answer: faq.answer,
            isActive: !faq.isActive,
            id: faq.id
        };

        try {
            const result = await post(`appconfigs/upsertfrequentlyaskedquestionasync`, faqUpdate);
            result.data === '' ? setErrored(true) : setErrored(false);

            const updatedFAQ = FAQ.map((item) => {
                if (item.id === result.data.id) {
                    const updatedItem = {
                        ...item,
                        isActive: result.data.isActive
                    };
                    item.isActive = result.data.isActive;
                    return updatedItem;
                }

                return item;
            });

            setFAQ(updatedFAQ);
        } catch (error) {
            setErrored(true);
        }

        setSnackbarArgument('toggle-faq');
        setShowSnackbar(true);
    };

    const handleContactToggle = async (contact: SupportContact) => {
        const contactUpdate: SupportContact = {
            supportType: contact.supportType,
            phoneNumber: contact.phoneNumber,
            email: contact.email,
            isActive: !contact.isActive,
            id: contact.id,
            supportInformationId: contact.supportInformationId
        };

        try {
            const result = await post(`appconfigs/upsertsupportcontactasync`, contactUpdate);
            result.data === '' ? setErrored(true) : setErrored(false);

            if (result?.data?.supportType === '') {
                const updatedContactList = TechnicalContact.map((item) => {
                    if (item.id === result.data.id) {
                        const updatedItem = {
                            ...item,
                            isActive: result.data.isActive
                        };
                        item.isActive = updatedItem.isActive;
                        return updatedItem;
                    }

                    return item;
                });

                setTechnicalContact(updatedContactList);
            }

            if (result?.data?.supportType !== '') {
                const updatedContactList = ClinicalContact.map((item) => {
                    if (item.id === result.data.id) {
                        const updatedItem = {
                            ...item,
                            isActive: result.data.isActive
                        };
                        return updatedItem;
                    }
                    return item;
                });

                setClinicalContact(updatedContactList);
            }
        } catch (error) {
            setErrored(true);
        }

        setSnackbarArgument('contact');
        setShowSnackbar(true);
    };

    const [expandedPanels, setExpandedPanels] = useState<string[]>([]);

    const handleChange = (isExpanded: boolean, panel: string) => {
        isExpanded
            ? setExpandedPanels((expandedPanels: string[]) => [
                  ...expandedPanels,
                  isExpanded ? panel : ''
              ])
            : setExpandedPanels((expandedPanels: string[]) =>
                  expandedPanels.filter((n: string) => !n.startsWith(panel))
              );
    };

    return (
        <>
            <StyledAccordionContainer
                expanded={
                    expandedPanels instanceof Array ? expandedPanels.includes(sectionTitle) : false
                }
                disableGutters={true}
                onChange={(_: unknown, isExpanded: boolean) =>
                    handleChange(isExpanded, sectionTitle)
                }>
                <AccordionSummary
                    expandIcon={
                        expandedPanels.includes(sectionTitle) ? <RemoveIcon /> : <AddIcon />
                    }>
                    <Typography variant="h3">{sectionTitle}</Typography>
                </AccordionSummary>

                <AccordionDetails>
                    <List>
                        {/* Clinical Support */}
                        <StyledInnerAccordion
                            sx={{ marginBottom: '0' }}
                            expanded={
                                expandedPanels instanceof Array
                                    ? expandedPanels.includes(clinicalSupportTitle)
                                    : false
                            }
                            disableGutters={true}
                            elevation={0}
                            onChange={(_: unknown, isExpanded: boolean) =>
                                handleChange(isExpanded, clinicalSupportTitle)
                            }>
                            <AccordionSummary
                                expandIcon={
                                    expandedPanels.includes(clinicalSupportTitle) ? (
                                        <RemoveIcon />
                                    ) : (
                                        <AddIcon />
                                    )
                                }>
                                <Typography variant="h4">{clinicalSupportTitle}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <List>
                                    <ListItem sx={innerBackground}>
                                        <ListItemText
                                            primary={
                                                <Typography variant="h4">
                                                    {availabilityTitle}
                                                </Typography>
                                            }
                                        />
                                        <AvailabilityModal
                                            availability={ClinicalAvailability}
                                            type={ClinicalSupportType}
                                            id={ClinicalSupportId}
                                            onUpsertAvailability={onUpsertAvailability}
                                        />
                                        <StyledGlobalSwitch
                                            sx={{ visibility: 'hidden' }}
                                            edge="end"
                                        />
                                    </ListItem>
                                    {ClinicalContact?.map((item, index) => (
                                        <ListItem key={index} sx={innerBackground}>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="h4">
                                                        Contact {index + 1}
                                                    </Typography>
                                                }
                                            />
                                            {/* edit item */}
                                            <ContactModal
                                                formData={item}
                                                type={ClinicalSupportType}
                                                onUpsertContact={onUpsertContact}
                                            />
                                            <StyledGlobalSwitch
                                                edge="end"
                                                onChange={(_: unknown) => handleContactToggle(item)}
                                                checked={item.isActive}
                                                inputProps={{
                                                    'aria-labelledby': `switch-list-label-contact`
                                                }}
                                            />
                                        </ListItem>
                                    ))}
                                    {/* add new item */}
                                    <ContactModal
                                        formData={emptyClinicalContactItem}
                                        type={ClinicalSupportType}
                                        onUpsertContact={onUpsertContact}
                                    />
                                </List>
                            </AccordionDetails>
                        </StyledInnerAccordion>

                        {/* Technical Support */}
                        <StyledInnerAccordion
                            sx={{ marginBottom: '0' }}
                            expanded={
                                expandedPanels instanceof Array
                                    ? expandedPanels.includes(technicalSupportTitle)
                                    : false
                            }
                            disableGutters={true}
                            elevation={0}
                            onChange={(_: unknown, isExpanded: boolean) =>
                                handleChange(isExpanded, technicalSupportTitle)
                            }>
                            <AccordionSummary
                                expandIcon={
                                    expandedPanels.includes(technicalSupportTitle) ? (
                                        <RemoveIcon />
                                    ) : (
                                        <AddIcon />
                                    )
                                }>
                                <Typography variant="h4">{technicalSupportTitle}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <List>
                                    <ListItem sx={innerBackground}>
                                        <ListItemText
                                            primary={
                                                <Typography variant="h4">
                                                    {availabilityTitle}
                                                </Typography>
                                            }
                                        />
                                        <AvailabilityModal
                                            availability={TechnicalAvailability}
                                            type={TechnicalSupportType}
                                            id={TechnicalSupportId}
                                            onUpsertAvailability={onUpsertAvailability}
                                        />
                                        <StyledGlobalSwitch
                                            sx={{ visibility: 'hidden' }}
                                            edge="end"
                                        />
                                    </ListItem>
                                    {TechnicalContact?.map((item, index) => (
                                        <ListItem key={index} sx={innerBackground}>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="h4">
                                                        Contact {index + 1}
                                                    </Typography>
                                                }
                                            />

                                            {/* edit item */}
                                            <ContactModal
                                                formData={item}
                                                type={TechnicalSupportType}
                                                onUpsertContact={onUpsertContact}
                                            />
                                            <StyledGlobalSwitch
                                                edge="end"
                                                onChange={(_: unknown) => handleContactToggle(item)}
                                                checked={item.isActive}
                                                inputProps={{
                                                    'aria-labelledby': `switch-list-label-contact`
                                                }}
                                            />
                                        </ListItem>
                                    ))}
                                    {/* add new item */}
                                    <ContactModal
                                        formData={emptyTechnicalContactItem}
                                        type={TechnicalSupportType}
                                        onUpsertContact={onUpsertContact}
                                    />
                                </List>
                            </AccordionDetails>
                        </StyledInnerAccordion>

                        {/* Frequently Asked Question */}
                        <StyledInnerAccordion
                            sx={{ marginBottom: '0' }}
                            expanded={
                                expandedPanels instanceof Array
                                    ? expandedPanels.includes(faqTitle)
                                    : false
                            }
                            disableGutters={true}
                            elevation={0}
                            onChange={(_: unknown, isExpanded: boolean) =>
                                handleChange(isExpanded, faqTitle)
                            }>
                            <AccordionSummary
                                expandIcon={
                                    expandedPanels.includes(faqTitle) ? <RemoveIcon /> : <AddIcon />
                                }>
                                <Typography variant="h4">{faqTitle}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <List>
                                    {FAQ?.map((item, index) => (
                                        <ListItem key={index} sx={innerBackground}>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="h4">
                                                        Question {index + 1}
                                                    </Typography>
                                                }
                                            />
                                            {/* edit existing faq item */}
                                            <QnAModal dto={item} onUpsertFAQ={onUpsertFAQ} />
                                            <StyledGlobalSwitch
                                                edge="end"
                                                onChange={(_: unknown) => handleFAQToggle(item)}
                                                checked={item.isActive}
                                                inputProps={{
                                                    'aria-labelledby': `switch-list-label-${item.question}`
                                                }}
                                            />
                                        </ListItem>
                                    ))}
                                    {/* add new faq item */}
                                    <QnAModal
                                        onUpsertFAQ={onUpsertFAQ}
                                        dto={{
                                            id: EMPTY_GUID,
                                            question: '',
                                            answer: '',
                                            isActive: true
                                        }}
                                    />
                                </List>
                            </AccordionDetails>
                        </StyledInnerAccordion>
                    </List>
                </AccordionDetails>
            </StyledAccordionContainer>

            <ConditionedSnackbar
                open={showSnackbar}
                handleClose={() => setShowSnackbar(false)}
                severity={!errored ? 'success' : 'error'}
                message={snackBarMessage(snackbarArgument)}
            />
        </>
    );
};

const innerBackground: SxProps<Theme> = () => ({
    backgroundColor: t.palette.listItemBackground.shade,
    marginBottom: '0.188rem'
});

export default SupportSection;
